import { GraphUtils } from '@helpers/graph.helper';
import { Router } from '@angular/router';
import { Sensor } from '@models/sensor/sensor';
import { UtilsHelper } from '@helpers/utils.helper';

export class CntParcialCumulativeAttribute extends Sensor {
  public cntParcialGraph: any;
  public cntParcialHourGraph: any;
  public cntParcialMonthGraph: any;
  public iconsToDisplay: { icon: string, type: number }[] = [];

  private cntParcialSensor: any[][] = [];
  private cntParcialHourSensor: any[][] = [];
  private cntParcialMonthSensor: any[][] = [];

  constructor(router: Router, url: string, alertsActive: boolean) {
    super(router, url, alertsActive);
    this.cntParcialGraph = null;
  }

  private ensureArraySize(arr: any[][], position: number): void {
    while (arr.length <= position) {
      arr.push([]);
    }
  }

  public hasCntParcialSensor(pos: number): boolean {
    const index = pos - 1;
    return (
      index >= 0 &&
      index < this.cntParcialSensor.length &&
      this.cntParcialSensor[index].length > 0
    );
  }

  public getCntParcialSensor(pos: number): any {
    const index = pos - 1;
    if (index >= 0 && index < this.cntParcialSensor.length) {
      const sensorData = this.cntParcialSensor[index];
      if (sensorData.length > 0) {
        return sensorData[sensorData.length - 1][1] ?? null;
      }
    }
    return null;
  }

  public hasHourCntParcialSensor(pos: number): boolean {
    const index = pos - 1;
    return (
      index >= 0 &&
      index < this.cntParcialHourSensor.length &&
      this.cntParcialHourSensor[index].length > 0
    );
  }

  public getHourCntParcialSensor(pos: number): any {
    const index = pos - 1;
    if (index >= 0 && index < this.cntParcialHourSensor.length) {
      const sensorData = this.cntParcialHourSensor[index];
      if (sensorData.length > 0) {
        return sensorData[sensorData.length - 1][1] ?? null;
      }
    }
    return null;
  }

  public hasMonthCntParcialSensor(pos: number): boolean {
    const index = pos - 1;
    return (
      index >= 0 &&
      index < this.cntParcialMonthSensor.length &&
      this.cntParcialMonthSensor[index].length > 0
    );
  }

  public getMonthCntParcialSensor(pos: number): any {
    const index = pos - 1;
    if (index >= 0 && index < this.cntParcialMonthSensor.length) {
      const sensorData = this.cntParcialMonthSensor[index];
      if (sensorData.length > 0) {
        return sensorData[sensorData.length - 1][1] ?? null;
      }
    }
    return null;
  }

  /** --- DATA PREPARATION --- **/

  public prepareData(): void {
    const iconMap = new Map<string, { icon: string, type: number }>();

    for (const element of this.data) {
      const pos = element.position ?? 0;
      const cntParcial = element.cntParcial ?? 0;
      const sensorInfo = UtilsHelper.nameSensor(element.type);
      const icon = sensorInfo.icon;

      if (!iconMap.has(icon)) {
        iconMap.set(icon, {
          icon,
          type: element.type
        });
      }

      this.ensureArraySize(this.cntParcialSensor, pos);
      this.ensureArraySize(this.cntParcialHourSensor, pos);
      this.ensureArraySize(this.cntParcialMonthSensor, pos);

      const date = new Date(element.date);
      const day = GraphUtils.getDay(date);
      const hour = GraphUtils.getHour(date);
      const month = GraphUtils.getMoth(date);

      // Día
      const currentDay = this.cntParcialSensor[pos].find(e => e[0] === day);
      if (currentDay) {
        currentDay[1] += cntParcial;
      } else {
        this.cntParcialSensor[pos].push([day, cntParcial]);
      }

      // Hora
      const currentHour = this.cntParcialHourSensor[pos].find(e => e[0] === hour);
      if (currentHour) {
        currentHour[1] += cntParcial;
      } else {
        this.cntParcialHourSensor[pos].push([hour, cntParcial]);
      }

      // Mes
      const currentMonth = this.cntParcialMonthSensor[pos].find(e => e[0] === month);
      if (currentMonth) {
        currentMonth[1] += cntParcial;
      } else {
        this.cntParcialMonthSensor[pos].push([month, cntParcial]);
      }
    }

    this.iconsToDisplay = Array.from(iconMap.values());
  }

  public setCharts(): void {
    // Diaria
    const filteredCntParcial = this.cntParcialSensor.filter(series => series.length > 0);
    const positionNodeCntParcial = this.cntParcialSensor
      .map((item, index) => (item.length > 0 ? index + 1 : -1))
      .filter(index => index !== -1);

    this.cntParcialGraph = this.getConfigChar(
      'cntParcialDaily',
      this.getChartSeriesNewNode('Sensor', filteredCntParcial, positionNodeCntParcial),
      this.getRanges('cnt_parcial_value_daily'),
      this.getLimits('cnt_parcial_value_daily'),
      'column'
    );

    const filteredHour = this.cntParcialHourSensor.filter(series => series.length > 0);
    const positionNodeHour = this.cntParcialHourSensor
      .map((item, index) => (item.length > 0 ? index + 1 : -1))
      .filter(index => index !== -1);

    this.cntParcialHourGraph = this.getConfigChar(
      'cntParcialHour',
      this.getChartSeriesNewNode('Sensor', filteredHour, positionNodeHour),
      this.getRanges('cnt_parcial_value_hour'),
      this.getLimits('cnt_parcial_value_hour'),
      'column'
    );

    const filteredMonth = this.cntParcialMonthSensor.filter(series => series.length > 0);
    const positionNodeMonth = this.cntParcialMonthSensor
      .map((item, index) => (item.length > 0 ? index + 1 : -1))
      .filter(index => index !== -1);

    this.cntParcialMonthGraph = this.getConfigChar(
      'cntParcialMonth',
      this.getChartSeriesNewNode('Sensor', filteredMonth, positionNodeMonth),
      this.getRanges('cnt_parcial_value_monthly'),
      this.getLimits('cnt_parcial_value_monthly'),
      'column'
    );
  }

  public static getVariables(): any[] {
    return [
      { title: 'Contador de pulso (acumulado) por hora', value: 'cnt_parcial_value_hour' },
      { title: 'Contador de pulso (acumulado) por día', value: 'cnt_parcial_value_daily' },
      { title: 'Contador de pulso (acumulado) por mes', value: 'cnt_parcial_value_monthly'}
    ];
  }
}
